import { useState, useMemo } from "react";
import { resolveTeam } from "./js/resolveTeam";
import { resolveIcon } from "./js/resolveIcon";
import { useForceReadability as resolveReadability } from "shared/src/hooks/useForceReadability";
import { resolveAriaLabel } from "./js/resolveAriaLabel";
import { useSize } from "shared/src/hooks/useSize";
import { isUseableType } from "shared/src/utils/isUseableType";
import { checkHasContainerQueries } from "shared/src/utils/supports";
import { useCompetitionType } from "shared/src/hooks/useCompetitionType";

import { CompetitorForceType, type CompetitorProps } from "./types";

import { CompetitorSkeleton } from "./components/CompetitorSkeleton";
import { CompetitorVertical } from "./components/CompetitorVertical";
import { CompetitorHorizontal } from "./components/CompetitorHorizontal";

const hasContainerQueries = checkHasContainerQueries();

export const defaultBreakpointConfig: Record<PropertyKey, CompetitorForceType> ={
  abbr: "abbreviation",
  middle: "shortname",
  full: "fullname"
};

export const Competitor = ({
  icon,
  country,
  displayName,
  jerseyNumber,
  position,
  logoType,
  force = null,
  team = null,
  usePreferredName = false,
  league: leagueProp,
  competition: competitionProp,
  sport: sportProp = "basketball",
  competitionType,
  iconSize,
  inverse = false,
  home = false,
  possession,
  rank,
  score,
  groupBy,
  description = "",
  useAvatar = false,
  avatar: _avatar,
  showAnimation = true,
  showIcon = true,
  showPlaceholder = true,
  center = false,
  scoreStacked = false,
  responsiveName = false,
  textSize = "body-sm",
  skeleton = false,
  tabbable = true,
  hasPlayerName,
  variant = "horizontal",
  breakpoints = {
    abbr: 140,
    full: 240,
  },
  breakpointConfig = defaultBreakpointConfig,
  compact = false,
  lineClamp = 2,
  mode = "light",
  className = "",
  forceReadability,
  inline,
  scoreSize,
  flipIcon,
  hasScorePadding,
  hideScorePadding,
  hideScore,
  textAlign = "left",
  transform,
  sync,
  ...rest
}: CompetitorProps) => {

  if (skeleton)
    return <CompetitorSkeleton inverse={inverse} iconSize={iconSize} />;

  const { sport, league } = useCompetitionType({ sport: sportProp, league: leagueProp, competition: competitionProp, competitionType });

  const [competitorRef, setCompetitorRef] = useState<HTMLDivElement | null>(
    null
  );

  // Resolve a team if passed in
  const {
    teamInfo,
    isWomens,
    hasTeam,
    hasFoundTeam,
    hasCountry,
    isTeamCountry,
    isDrawTeam,
    isFlagAllowed,
    _team,
    _displayName,
  } = useMemo(() => resolveTeam({ team, displayName, country, sport, league, competitionType }), [team, displayName, country, sport, league, competitionType]);

  // Resolve the Icon to use
  const { showComputedIcon, activeIcon, _iconSize } = resolveIcon({
    _avatar,
    showIcon,
    variant,
    country,
    iconSize,
    sport,
    league,
    competitionType,
    mode,
    hasFoundTeam,
    _team,
    teamInfo,
    hasCountry,
    isTeamCountry,
    icon,
    flipIcon,
    hasTeam,
    isDrawTeam,
    isFlagAllowed,
    useAvatar,
    position,
    transform,
    jerseyNumber,
    logoType
  });

  // Fallback to ResizeObserver when no Container query support
  const { width } = useSize(competitorRef, 100, hasContainerQueries) || {
    width: "auto",
    height: "auto",
  };

  const hasScore = isUseableType(score) || typeof possession === "boolean" || sync !== undefined;

  // Resolve the computed aria-label
  const ariaLabel = resolveAriaLabel({
    teamInfo,
    _displayName,
    description,
    hasScore,
    score,
    possession,
  });

  // Resolve readable tokens (this makes sure text is readable on whatever background it's on)
  const { readableToken, hasForceReadability } = resolveReadability({
    forceReadability,
    teamInfo,
  });

  // console.log("Rendering Competitor", team, displayName, {width, height})

  const scoreWithPossesionProps = {
    score,
    compact,
    possession,
    showAnimation,
    inverse,
    sport,
    variant,
    hasScore,
    size: scoreStacked ? "xl" :scoreSize,
    scoreStacked,
    sync
  };
  const competitorNameProps = {
    displayName: _displayName,
    teamInfo,
    force,
    width,
    rank,
    textSize,
    inverse,
    breakpoints,
    breakpointConfig,
    hasContainerQueries,
    scoreStacked,
    isWomens,
    lineClamp,
    variant,
    textAlign,
    responsiveName,
    sport
  };

  const competitorWrapperProps = {
    variant,
    showComputedIcon,
    activeIcon,
    className,
    ariaLabel,
    tabbable,
    compact,
    scoreStacked,
    inline,
    sport,
    rest,
  };

  const competitorVariantProps = {
    wrapperProps: competitorWrapperProps,
    showComputedIcon,
    activeIcon,
    _iconSize,
    inverse,
    rank,
    readableToken,
    hasForceReadability,
    competitorNameProps,
    scoreWithPossesionProps,
    hasScore,
    description,
    variant,
    center,
    compact,
    scoreStacked,
    hasContainerQueries,
    inline,
    sport,
    competitionType,
    showPlaceholder,
    hideScore,
    hasScorePadding,
    hideScorePadding,
    sync,
    setCompetitorRef,
  };

  if (variant === "vertical") return <CompetitorVertical {...competitorVariantProps} />;
  return <CompetitorHorizontal {...competitorVariantProps} />;
};